import type { AlertBannerCardProps } from '../../../types/alerts.d';
import './AlertBannerCard.scss';

import {
	faArrowsMaximize,
	faBadgeCheck,
	faMapMarker,
} from '@fortawesome/pro-light-svg-icons';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { Chip } from '@hdir/ui/chip';
import { DropdownMenuDivider } from '@hdir/ui/dropdown-menu';
import { Spinner } from '@hdir/ui/spinner';
import cx from 'clsx';
import React, { forwardRef, useEffect, useRef, useState } from 'react';

import { Button, Div } from '/react4xp/components/Element';
import { Lightbox } from '/react4xp/shared/Lightbox/Lightbox';
import { formatDate } from '/react4xp/components/DateComponent';
import { FontAwesomeIconWrapper } from '/react4xp/components/FontAwesomeIconWrapper';
import { safeHtmlDecode } from '/react4xp/util/safeHtmlDecode';

import { AlertBannerLabel } from '../AlertBannerLabel/AlertBannerLabel';
import { IconWithText } from '../IconWithText/IconWithText';
import Richtext from '../Richtext/Richtext';
import { Row } from '../Row';

import { openLightboxWithKeys } from './openLightboxWithKeys';
import { DateComponent } from './DateComponent';
import { HEADER } from './constants';
import { PrintHelpDialog } from './PrintHelpDialog';
import { getAlertBannerType } from './getAlertBannerType';


export const AlertBannerCardAccordion = forwardRef<
	HTMLDivElement,
	Pick< // Only want the CardAccordion ones, not the CardLink ones.
		AlertBannerCardProps,
		| 'content'
		| 'date'
		| 'empty'
		| 'image'
		| 'isHighlighted'
		| 'location'
		| 'showSpinner'
		| 'title'
		| 'alertType'
	>
>((
	{
		content,
		date,
		empty,
		image,
		isHighlighted,
		location,
		showSpinner,
		title,
		alertType,
	},
	refFromParent,
) => {
	const { isDrugAnalyzed } = content;

	const alertRef = useRef<HTMLDivElement | null>(null);

	const [isOpen, setIsOpen] = useState(false);

	const [lightboxImage, _setLightboxImage] = useState<string | null>(null);

	function setLightboxImage(value: React.SetStateAction<string | null>): void {
		if (!empty) _setLightboxImage(value);
	};

	useEffect(() => {
		if (isHighlighted) {
			setIsOpen(true); // Open if highlighted
		}
	}, [isHighlighted]);

	const toggleAccordion = (): void => {
		if (!empty) setIsOpen(!isOpen);
	};

	const handleEnterOrSpace = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
		if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
			e.preventDefault();
			e.stopPropagation();
			toggleAccordion();
		}
	};
	return (
		<div
			className={cx(
				'alert-banner-card',
				{ 'pointer-events-none': empty },
				'relative', // To position Spinner absolutely
			)}
			data-state={isOpen ? 'open' : 'closed'}
			ref={(htmlDivElement) => {
				if (typeof refFromParent === 'function') {
					refFromParent(htmlDivElement);
				} else if (refFromParent) {
					refFromParent.current = htmlDivElement;
				}
				alertRef.current = htmlDivElement;
			}}
		>
			<Div
				className={[
					'alert-banner-card__header',
					HEADER.NORMAL,
					getAlertBannerType(alertType, date.isExpired),
				]}
			>
				{image &&
					<figure
						className={`lightbox-image lightbox-image__header-wrapper${empty ? ' opacity-0': ''}`}
						onClick={() => setLightboxImage(image.url)}
						onKeyDown={(e) => openLightboxWithKeys(e, () => setLightboxImage(image.url))}
						tabIndex={empty ? -1 : 0}
						role="button"
						aria-label="Åpne bildevisning"
					>
						<img className="lightbox-image__header-wrapper__image"
							src={image.url}
							alt="Bilde av stoff"
							loading="eager"
							draggable={false}
						/>
						<span className="lightbox-image__header-wrapper__icon">
							<FontAwesomeIconWrapper
								className='text-white'
								fixedWidth={true}
								icon={faArrowsMaximize}
							/>
						</span>
					</figure>}
				<Button onKeyDown={handleEnterOrSpace}
					className={[
						'alert-banner-card__header__container',
						'bg-[inherit]',
						date.isExpired ? 'expired' : 'active',
					]}
					onClick={toggleAccordion}
				>
					<Div
						className={[
							'alert-banner-card__header__content',
							{ 'opacity-0' : empty },
						]}
					>
						<div className="chips">
							<AlertBannerLabel
								className={date.isExpired ? 'expired' : 'active'}
								alertType={alertType}
							/>
							{date.isExpired ?
								<Chip
									size={"large"}
									color="grey"
									isRemovable={false}
									className="text-sm h-full"
								>
									Inaktiv
								</Chip> :
								null
							}
						</div>
						{title && <h2>{title}</h2>}
						<DateComponent date={date} />
						{location && <IconWithText icon={faMapMarker} text={location}/>}
					</Div>
					<FontAwesomeIconWrapper
						className={`alert-banner-card__header__icon${empty ? ' opacity-0' : ''}`}
						fixedWidth={true}
						icon={faAngleDown}
					/>
				</Button>
			</Div>
			<Div
				className={[
					'alert-banner-card__content',
					date.isExpired
						? 'border-t-sm border-t-solid border-t-[#767676]'
						: 'border-[#A0002E]',
				]}
			>
				<div className="alert-banner-card__content__text">
					<div>
						<h3>Beskrivelse</h3>
						{/* Richtext is used because description can contain bulletPoints */}
						<Richtext text={content.description}/>
					</div>
					<div>
						<h3>Utseende</h3>
						{safeHtmlDecode(content.appearance)}
					</div>
					<div>
						<h3>Anbefalinger og forhåndsregler</h3>
						{/* Richtext is used because recommendation can contain bulletPoints */}
						<Richtext text={content.recommendation}/>
					</div>
					<IconWithText
						className="color-text-neutral-subtle text-balance"
						icon={faBadgeCheck}
						text="Informasjonen er kvalitetssikret av Helsedirektoratet"
					/>
				</div>
				<div className="flex flex-wrap gap-sm justify-between">
					<div className="flex flex-wrap gap-sm items-center">
						{
							<Chip
								className={`${empty ? 'opacity-0 ' : ''}text-wrap`}
								color={isDrugAnalyzed ? 'green' : 'orange'}
								isRemovable={false}
							>
								{`${
									isDrugAnalyzed ? 'Bekreftet:' : 'Mistanke:'
								} ${
									safeHtmlDecode(content.drugTypeSpecific || content.drugType)
								}`}
							</Chip>
						}
						{!!content.drugForm &&
						<Chip
							className={`${empty ? 'opacity-0 ' : ''}text-wrap`}
							color="grey"
							isRemovable={false}
						>
							{safeHtmlDecode(content.drugForm)}
						</Chip>}
					</div>
					{date.isModified && <p className="text-neutral-subtle flex align-middle leading-4">Publisert: {formatDate(date.published)}</p>}
				</div>
				{
					date.isExpired ? null : <>
						<DropdownMenuDivider className="m-0"/>
						<Row
							className='md:justify-end'
						>
							<PrintHelpDialog
								content={content}
								date={date}
								title={title}
								image={image}
							/>
						</Row>
					</>
				}
			</Div>
			{showSpinner ? <Spinner className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' size='large'/> : null}
			<Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
		</div>
	);
});
